import { v4 as uuid } from 'uuid';
import Vue from 'vue';

export default {
  namespaced: true,

  state: {
    list: null,
    current: null,
    tab: 'basic',
    basicForm: null,
    propertyForm: null,
  },

  getters: {
    current(state) {
      return state.current;
    },

    list: (state) => state.list,

    get(state) {
      return (id, reload) => {
        let found = null;

        if (state.list) {
          found = state.list.find((i) => i.id === id);
        }

        if (!found || !reload) {
          if (state.current && state.current.id === id) {
            found = state.current;
          }
        }

        return found;
      };
    },
  },

  mutations: {
    tab(state, payload) {
      state.tab = payload;
    },

    basicForm(state, payload) {
      state.basicForm = payload;
    },

    propertyForm(state, payload) {
      state.propertyForm = payload;
    },

    bind(state, payload) {
      state.list = payload;
    },

    orderUp(state, payload) {
      const entities = state.list;

      entities.sort(
        (a, b) =>
          Number(a.order || 0) - Number(b.order || 0),
      );

      entities.forEach((entity, index) => {
        Vue.set(entity, 'order', index + 1);
      });

      const current = entities.find(
        (entity) => entity.id === payload.id,
      );

      if (!current) {
        return;
      }

      const previous = entities.find(
        (entity) => entity.order === current.order - 1,
      );

      Vue.set(previous, 'order', previous.order + 1);
      Vue.set(current, 'order', current.order - 1);
    },

    orderDown(state, payload) {
      const entities = state.list;

      entities.sort(
        (a, b) =>
          Number(a.order || 0) - Number(b.order || 0),
      );

      entities.forEach((entity, index) => {
        Vue.set(entity, 'order', index + 1);
      });

      const current = entities.find(
        (entity) => entity.id === payload.id,
      );

      if (!current) {
        return;
      }

      const next = entities.find(
        (entity) => entity.order === current.order + 1,
      );

      Vue.set(next, 'order', next.order - 1);
      Vue.set(current, 'order', current.order + 1);
    },

    copy(state, payload) {
      const entities = state.list;

      const originalEntity = entities.find(
        (entity) => entity.id === payload.id,
      );

      const newEntity = JSON.parse(
        JSON.stringify(originalEntity),
      );

      newEntity.id = uuid();
      newEntity.name.singular = `${newEntity.name.singular}Copy`;
      newEntity.name.plural = `${newEntity.name.plural}Copy`;
      newEntity.label.singular = `${newEntity.label.singular} (Copy)`;
      newEntity.label.plural = `${newEntity.label.plural} (Copy)`;

      for (let field of newEntity.fields) {
        const isDisplay =
          field.id === newEntity.properties?.display;

        field.id = uuid();

        if (isDisplay) {
          newEntity.properties.display = field.id;
        }
      }

      entities.push(newEntity);
    },

    persist(state) {
      const entities = state.list;

      const index = entities.findIndex(
        (entity) => entity.id === state.current.id,
      );

      const isMustSetOrder =
        entities.length &&
        entities[0].order &&
        !state.current.order;

      if (isMustSetOrder) {
        Vue.set(state.current, 'order', entities.length);
      }

      if (index > -1) {
        entities.splice(index, 1, state.current);
      } else {
        entities.push(state.current);
      }
    },

    new(state) {
      state.current = {
        id: uuid(),
        name: {
          singular: null,
          plural: null,
        },
        label: {
          singular: null,
          plural: null,
        },
        properties: {},
        fields: [],
      };

      state.tab = 'basic';
    },

    edit(state, payload) {
      state.current = JSON.parse(JSON.stringify(payload));
      state.tab = 'basic';
    },

    delete(state, payload) {
      const entities = state.list;

      if (!entities || !entities.length) {
        return;
      }

      const index = entities.findIndex(
        (e) => e.id === payload.id,
      );

      if (index > -1) {
        entities.splice(index, 1);
      }
    },

    clear(state) {
      state.current = null;
      state.tab = 'basic';
    },

    mergePropertiesDefaults(state, defaults) {
      state.current.properties = Object.assign(
        defaults,
        state.current.properties,
      );
    },
  },

  actions: {
    new({ commit, getters, rootGetters }) {
      commit('new');
      commit(
        'builder/fieldCurrent/bindEntity',
        getters.current,
        { root: true },
      );
      commit(
        'mergePropertiesDefaults',
        rootGetters['builder/entityProperty/defaults'],
      );
    },

    edit({ commit, getters }, payload) {
      commit('edit', payload);
      commit(
        'builder/fieldCurrent/bindEntity',
        getters.current,
        { root: true },
      );
    },

    orderUp({ commit, dispatch }, payload) {
      commit('orderUp', payload);
      dispatch('builder/persistStateToLocalStorage', null, {
        root: true,
      });
    },

    orderDown({ commit, dispatch }, payload) {
      commit('orderDown', payload);
      dispatch('builder/persistStateToLocalStorage', null, {
        root: true,
      });
    },

    clear({ commit }) {
      commit('clear');
      commit('builder/fieldCurrent/bindEntity', null, {
        root: true,
      });
    },

    copy({ commit, dispatch }, payload) {
      return commit('copy', payload).then(() =>
        dispatch(
          'builder/persistStateToLocalStorage',
          null,
          { root: true },
        ),
      );
    },

    save({ commit, dispatch }) {
      return dispatch('validate')
        .then((valid) => (!valid ? Promise.reject() : null))
        .then(() => commit('persist'))
        .then(() =>
          dispatch(
            'builder/persistStateToLocalStorage',
            null,
            { root: true },
          ),
        );
    },

    delete({ commit, dispatch }, payload) {
      commit('delete', payload);
      return dispatch(
        'builder/persistStateToLocalStorage',
        null,
        { root: true },
      );
    },

    validate({ state, rootGetters, commit, dispatch }) {
      const validateBasicForm = new Promise((resolve) => {
        if (!state.basicForm) {
          resolve(true);
          return;
        }

        state.basicForm.validate((valid) => {
          resolve(valid);
        });
      });

      const validatePropertyForm = new Promise(
        (resolve) => {
          if (!state.propertyForm) {
            resolve(true);
            return;
          }

          state.propertyForm.validate((valid) => {
            resolve(valid);
          });
        },
      );

      const validateFields = new Promise((resolve) => {
        const enabled =
          rootGetters['builder/field/enabled'];

        if (!enabled) {
          resolve(true);
          return;
        }

        const valid =
          state.current &&
          state.current.fields &&
          state.current.fields.length > 0;
        resolve(valid);
      });

      return Promise.all([
        validateBasicForm,
        validatePropertyForm,
        validateFields,
      ]).then((valids) => {
        const allValid = valids.every((v) => !!v);

        if (allValid) {
          return true;
        }

        if (!valids[0]) {
          commit('tab', 'basic');
          return false;
        }

        if (!valids[1]) {
          commit('tab', 'property');
          return false;
        }

        if (!valids[2]) {
          commit('tab', 'field');

          dispatch(
            'core/validationError',
            new Error(
              'At least one custom field is required',
            ),
            { root: true },
          );

          return false;
        }

        commit('tab', 'basic');
        return false;
      });
    },
  },
};
